<p-toast></p-toast>
<p-progressBar mode="indeterminate" class="m-2" *ngIf="loading()"></p-progressBar>

@if (quotation(); as quote) {
<app-page-shell>
<div class="quotation-page">

    <app-page-detail-hero [auditStamp]="{ createdAt: quote.createdAt, updatedAt: quote.updatedAt, createdBy: quote.createdByCustomerId, updatedBy: quote.updatedByCustomerId, createdByActor: quote.createdByActor, updatedByActor: quote.updatedByActor }"
        eyebrow="CRM Quotations"
        [title]="quote.subject || 'Quotation Details'"
        [subtitle]="(quote.customerName || '-') + (quote.pocName ? ' / ' + quote.pocName : '')"
        icon="pi pi-file">
        <div tags class="quotation-hero-tags">
            <div class="quotation-hero-status" [ngClass]="'status-' + ((quote.status || 'PREPARING').toLowerCase().replaceAll('_', '-'))">
                <span><i class="pi pi-circle-fill"></i> Quotation Status</span>
                <strong>{{ quote.status || 'PREPARING' }}</strong>
            </div>
            <div class="quotation-hero-meta-tags">
                <p-tag [value]="quote.jobType || quote.type || 'GENERAL'" severity="info"></p-tag>
                <p-tag [value]="quote.deviationStatus || 'NO_CRITERIA'" severity="warn"></p-tag>
            </div>
        </div>
        <div actions>
            @if (quote.status === 'PREPARING') {
                <p-button label="Move to Submission" icon="pi pi-arrow-right" severity="success" (onClick)="moveToSubmission()"></p-button>
            }
            @if (quote.status === 'INTERNAL_REVIEW') {
                <p-button label="Mark Ready" icon="pi pi-check-circle" severity="success" (onClick)="markReady()"></p-button>
            }
            @if (quote.status === 'READY_TO_SUBMIT') {
                <p-button label="Submit to Client" icon="pi pi-send" severity="success" (onClick)="submitToClient()"></p-button>
            }
            @if (quote.status === 'SUBMITTED') {
                <p-button label="Reject" icon="pi pi-times" severity="danger" [outlined]="true" (onClick)="showRejectDialog.set(true)"></p-button>
                <p-button label="Approve" icon="pi pi-check" severity="warn" (onClick)="approveQuotation()"></p-button>
            }
            @if (latestGeneratedPdf(); as pdf) {
                <p-button [label]="'PDF v' + (pdf.versionNumber || 1)" icon="pi pi-download" severity="info" [loading]="pdfGenerating()" (onClick)="downloadPdf()"></p-button>
                <p-button label="Regenerate PDF" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="pdfGenerating()" (onClick)="regeneratePdf()"></p-button>
            } @else {
                <p-button label="Generate PDF" icon="pi pi-download" severity="info" [loading]="pdfGenerating()" (onClick)="downloadPdf()"></p-button>
            }
            @if (quote.status === 'APPROVED' || quote.status === 'JOB') {
                <p-button label="Email" icon="pi pi-envelope" severity="success" (onClick)="sendEmail()"></p-button>
            }
            @if (canConvertToProject()) {
                <p-button label="Convert" icon="pi pi-external-link" [outlined]="true" (onClick)="convertToProject()"></p-button>
            }
            @if (canCreateContract()) {
                <p-button label="Create Contract" icon="fa-solid fa-file-contract" severity="success" [outlined]="true" (onClick)="createContract()"></p-button>
            }
            @if (canCreateSalesOrder()) {
                <p-button label="Create Sales Order" icon="pi pi-file-plus" severity="info" [outlined]="true" (onClick)="createSalesOrder()"></p-button>
            }
            @if (isCfQuote(quote)) {
                <p-button label="Create C&F Execution" icon="pi pi-compass" severity="help" [outlined]="true" (onClick)="openCfExecutionHandoff()"></p-button>
            }
            <p-button label="Create Logistics Project" icon="pi pi-briefcase" severity="secondary" [outlined]="true" (onClick)="createLogisticsProjectFromQuotation()"></p-button>
            @if (canCreateContract() && contractLinks().length > 0) {
                <p-button label="Create Amendment" icon="fa-solid fa-file-pen" severity="secondary" [outlined]="true" (onClick)="createAmendment()"></p-button>
            }
            <p-button label="Workflow Config" icon="pi pi-sitemap" severity="secondary" [outlined]="true" [routerLink]="['/workflow/config']" [queryParams]="{ resourceKey: 'CRM_QUOTATION' }"></p-button>
            <p-button label="Edit" icon="pi pi-pencil" severity="secondary" [outlined]="true" [routerLink]="['/crm/quotations', quote.id, 'edit']"></p-button>
            <p-button label="Save" icon="pi pi-save" (onClick)="saveDetails()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="detailMetrics()"></app-page-metric-strip>

    <app-page-data-panel
        title="Quotation Workspace"
        subtitle="Customer, job, pricing, documents, communication, remarks, and workflow in one operating view."
        [resultText]="quote.quotationNumber || 'DRAFT'">
        <nav class="quotation-workspace-tabs" aria-label="Quotation sections">
            @for (tab of visibleTabs(); track tab.id) {
                <button type="button" class="workspace-tab-button" [class.active]="activeTab() === tab.id" (click)="setTab(tab.id)">
                    <span class="workspace-tab">
                        <i [class]="tab.icon"></i>
                        <span>{{ tab.label }}</span>
                        @if (tab.id === 'general') { <span class="workspace-badge info">{{ quote.status }}</span> }
                        @if (tab.id === 'job') { <span class="workspace-badge neutral">{{ quote.jobType || quote.type || 'Job' }}</span> }
                        @if (tab.id === 'lines') { <span class="workspace-badge info">{{ lineControls.length }}</span> }
                        @if (tab.id === 'documents') { <span class="workspace-badge success">{{ attachments().length }}</span> }
                        @if (tab.id === 'communication') { <span class="workspace-badge info">{{ externalReviews().length }}</span> }
                        @if (tab.id === 'remarks') { <span class="workspace-badge neutral">{{ reviewNotes().length }}</span> }
                        @if (tab.id === 'tracking') { <span class="workspace-badge success">{{ requirements().length + tasks().length }}</span> }
                    </span>
                </button>
            }
        </nav>

        @if (quote.status === 'REJECTED') {
            <div class="quote-status-banner danger">
                <i class="pi pi-exclamation-circle"></i>
                <div><b>Quotation Rejected</b><span>{{ quote.rejectionReason || 'No reason recorded' }}</span></div>
            </div>
        }

        @if (quote.status === 'APPROVED') {
            <div class="quote-status-banner success">
                <i class="pi pi-check-circle"></i>
                <div><b>Quotation Approved</b><span>Approved by User #{{ quote.approvedByUserId }} on {{ quote.approvedAt | date:'medium' }}</span></div>
            </div>
        }

        @if (contractLinks().length > 0 || salesOrderLinks().length > 0) {
            <div class="linked-record-strip">
                <span>Linked Records</span>
                @for (link of contractLinks(); track link.linkId) {
                    <a [routerLink]="['/contracts/view', link.contractId]"><i class="fa-solid fa-file-contract"></i>{{ link.contractNumber || ('Contract #' + link.contractId) }}</a>
                }
                @for (order of salesOrderLinks(); track order.orderID) {
                    <a [routerLink]="['/order/sales-order/view', order.orderID]"><i class="pi pi-file-edit"></i>{{ order.orderNumber || ('Sales Order #' + order.orderID) }}</a>
                }
            </div>
        }

        <div class="workspace-section">
        @if (activeTab() === 'general') {
            <div class="grid">
                <div class="col-12 lg:col-8">
                    <div class="surface-card border-round-lg border-1 surface-border p-4">
                        <div class="section-heading">
                            <i class="pi pi-info-circle text-primary"></i>
                            <span>General Details</span>
                        </div>
                        <div class="grid p-fluid" [formGroup]="generalForm">
                            <div class="field col-12 md:col-6" [class.validation-field-invalid]="isInvalid(generalForm, 'subject')">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-file-edit"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="subject" formControlName="subject" />
                                        <label for="subject">Quotation Subject *</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                @if (isInvalid(generalForm, 'subject')) {
                                    <small class="p-error block mt-1">Quotation subject is required.</small>
                                }
                            </div>
                            <div class="field col-12 md:col-6" [class.validation-field-invalid]="isInvalid(generalForm, 'sourceKey')">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select inputId="sourceKey" formControlName="sourceKey" [options]="sourceOptions()" optionLabel="label" optionValue="value" [filter]="true" appendTo="body" styleClass="w-full"></p-select>
                                        <label for="sourceKey">Customer Source *</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                @if (isInvalid(generalForm, 'sourceKey')) {
                                    <small class="p-error block mt-1">Select a lead, prospect, or client.</small>
                                }
                            </div>
                            <div class="field col-12">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <textarea pTextarea id="description" formControlName="description" rows="3"></textarea>
                                        <label for="description">Description</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-globe"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select inputId="countryId" formControlName="countryId" [options]="countries()" optionLabel="name" optionValue="id" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                                        <label for="countryId">Location Country</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select inputId="cityId" formControlName="cityId" [options]="cityOptions()" optionLabel="cityName" optionValue="id" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" [disabled]="!generalForm.get('countryId')?.value">
                                            <ng-template pTemplate="selectedItem" let-city>{{ city ? cityLabel(city) : '' }}</ng-template>
                                            <ng-template pTemplate="item" let-city>{{ cityLabel(city) }}</ng-template>
                                        </p-select>
                                        <label for="cityId">Location City</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-8" [class.validation-field-invalid]="isInvalid(generalForm, 'pocName')">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select inputId="contactCustomerId" formControlName="customerId" [options]="customerOptions()" [optionLabel]="'name'" optionValue="id" [filter]="true" [showClear]="true" [loading]="contactResolving()" appendTo="body" styleClass="w-full">
                                            <ng-template pTemplate="selectedItem" let-customer>{{ customer ? contactLabel(customer) : '' }}</ng-template>
                                            <ng-template pTemplate="item" let-customer>{{ contactLabel(customer) }}</ng-template>
                                        </p-select>
                                        <label for="contactCustomerId">Quotation Contact *</label>
                                    </p-iftaLabel>
                                    <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openContactDialog()"></p-button>
                                </p-inputGroup>
                                @if (isInvalid(generalForm, 'pocName')) {
                                    <small class="p-error block mt-1">Select or create a quotation contact.</small>
                                }
                            </div>
                            <div class="field col-12 md:col-4">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="pocEmail" formControlName="pocEmail" readonly />
                                        <label for="pocEmail">Contact Email</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-4">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select inputId="status" formControlName="status" [options]="quotationStatuses" optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
                                        <label for="status">Status</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-12 lg:col-4">
                    <div class="surface-card border-round-lg border-1 surface-border p-4 h-full">
                        <div class="section-heading">
                            <i class="pi pi-chart-line text-primary"></i>
                            <span>Summary</span>
                        </div>
                        <div class="summary-grid">
                            <div><span>Quotation #</span><b>{{ quote.quotationNumber || '-' }}</b></div>
                            <div><span>Job Type</span><b>{{ quote.jobType || quote.type || '-' }}</b></div>
                            <div><span>Location</span><b>{{ quotationCityLabel(quote) }}</b></div>
                            <div><span>Contact</span><b>{{ quote.contactCustomer?.name || quote.pocName || '-' }}</b></div>
                            <div><span>Sales / KAM</span><b>{{ quote.salesTeamName || quote.kamCustomerId || '-' }}</b></div>
                            <div><span>Start Date</span><b>{{ quote.startDate | date:'mediumDate' }}</b></div>
                            <div><span>Expiry Date</span><b>{{ quote.expiryDate | date:'mediumDate' }}</b></div>
                            <div><span>Currency</span><b>{{ quote.currencyCode || 'SAR' }} / {{ quote.exchangeRate || 1 }}</b></div>
                            <div><span>Deviation</span><b>{{ quote.deviationStatus || 'NO_CRITERIA' }}</b></div>
                            <div><span>Total Amount</span><b class="text-primary text-xl">{{ quote.totalAmount | currency:'SAR ' }}</b></div>
                        </div>
                        @if (quote.deviationSummary) {
                            <div class="mt-3 text-sm text-600">{{ quote.deviationSummary }}</div>
                        }
                    </div>
                </div>
            </div>
        }

        @if (activeTab() === 'job' && isCfQuote(quote)) {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-compass text-primary"></i>
                    <span>C&F Execution Details</span>
                </div>
                <div class="job-summary-grid mb-3">
                    <div><span>Service Scope</span><b>{{ quote.logisticsDetails?.serviceScope || '-' }}</b></div>
                    <div><span>Shipment Mode</span><b>{{ quote.logisticsDetails?.shipmentMode || '-' }}</b></div>
                    <div><span>Import / Export</span><b>{{ quote.logisticsDetails?.importExportType || '-' }}</b></div>
                    <div><span>Route</span><b>{{ (quote.logisticsDetails?.originPort || '-') + ' -> ' + (quote.logisticsDetails?.destinationPort || '-') }}</b></div>
                    <div><span>BL / AWB</span><b>{{ quote.logisticsDetails?.blAwbNumber || '-' }}</b></div>
                    <div><span>HBL / HAWB</span><b>{{ quote.logisticsDetails?.hblHawbNumber || '-' }}</b></div>
                    <div><span>Vessel / Flight</span><b>{{ quote.logisticsDetails?.vesselOrFlight || '-' }}</b></div>
                    <div><span>Carrier</span><b>{{ quote.logisticsDetails?.carrier || '-' }}</b></div>
                    <div><span>ETD</span><b>{{ quote.logisticsDetails?.etd | date:'mediumDate' }}</b></div>
                    <div><span>ETA</span><b>{{ quote.logisticsDetails?.eta | date:'mediumDate' }}</b></div>
                    <div><span>Delivery Order</span><b>{{ quote.logisticsDetails?.deliveryOrderNumber || '-' }}</b></div>
                    <div><span>Bayan</span><b>{{ quote.logisticsDetails?.bayanNumber || '-' }}</b></div>
                </div>
                <div class="job-summary-grid mb-3">
                    <div><span>Create Clearance</span><b>{{ quote.logisticsDetails?.clearanceRequired === false ? 'No' : 'Yes' }}</b></div>
                    <div><span>Transport Required</span><b>{{ quote.logisticsDetails?.transportRequired === false ? 'No' : 'Yes' }}</b></div>
                    <div><span>Container Tracking</span><b>{{ quote.logisticsDetails?.containerTrackingRequired ? 'Yes' : 'No' }}</b></div>
                    <div><span>Undertaking</span><b>{{ quote.logisticsDetails?.undertakingNumber || '-' }}</b></div>
                </div>
                @if (quote.logisticsDetails?.cargoDescription) {
                    <div class="text-sm text-600 mb-2">{{ quote.logisticsDetails?.cargoDescription }}</div>
                }
                @if (quote.logisticsDetails?.deliveryAddress) {
                    <div class="text-sm text-600">{{ quote.logisticsDetails?.deliveryAddress }}</div>
                }
            </div>
        }

        @if (activeTab() === 'job' && isLogisticsQuote(quote)) {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-truck text-primary"></i>
                    <span>Logistics Delivery Rate Cards</span>
                </div>
                @if (quote.logisticsDetails?.duration || quote.logisticsDetails?.workingHours || quote.logisticsDetails?.scope || quote.logisticsDetails?.quantity || quote.paymentTerms || quote.logisticsDetails?.exclusions) {
                    <div class="job-summary-grid mb-3">
                        <div><span>Duration</span><b>{{ quote.logisticsDetails?.duration || '-' }}</b></div>
                        <div><span>Working Hours</span><b>{{ quote.logisticsDetails?.workingHours || '-' }}</b></div>
                        <div><span>Transport Quantity</span><b>{{ quote.logisticsDetails?.quantity || 1 }}</b></div>
                        <div><span>Payment Terms</span><b>{{ quote.paymentTerms || '-' }}</b></div>
                        <div><span>Exclusions</span><b>{{ quote.logisticsDetails?.exclusions || '-' }}</b></div>
                    </div>
                    @if (quote.logisticsDetails?.scope) {
                        <div class="text-sm text-600 mb-3">{{ quote.logisticsDetails?.scope }}</div>
                    }
                }
                <p-table [value]="logisticsRateCardRows()" styleClass="p-datatable-sm p-datatable-gridlines logistics-rate-table" [tableStyle]="{ 'min-width': '72rem' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th style="width: 8rem">Rate</th>
                            <th style="width: 10rem">Type</th>
                            <th>Lane</th>
                            <th>Vehicle</th>
                            <th style="width: 9rem">Amount</th>
                            <th style="width: 9rem">Transit</th>
                            <th style="width: 11rem">Waiting</th>
                            <th style="width: 11rem">Included Terms</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-rate>
                        <tr>
                            <td><strong>{{ rate.rateId ? ('#' + rate.rateId) : 'Draft' }}</strong></td>
                            <td>
                                <strong>{{ rate.rateType || '-' }}</strong>
                                <div class="text-sm text-500">{{ rate.unitBasis || '-' }}</div>
                            </td>
                            <td>
                                <strong>{{ rateLane(rate) }}</strong>
                                @if (rate.distance) {
                                    <div class="text-sm text-500">{{ rate.distance | number:'1.0-2' }} km</div>
                                }
                            </td>
                            <td>{{ rateVehicle(rate) }}</td>
                            <td><strong>{{ quote.currencyCode || 'SAR' }} {{ (rate.amount || 0) | number:'1.2-2' }}</strong></td>
                            <td>{{ rate.transitTime || '-' }}</td>
                            <td>
                                <div>{{ quote.currencyCode || 'SAR' }} {{ (rate.waitingCharge || 0) | number:'1.2-2' }}</div>
                                <div class="text-sm text-500">{{ rate.waitingThreshold || 0 }} mins</div>
                            </td>
                            <td>
                                <span>Insurance: {{ rateIncluded(rate.goodsInsurance) }}</span><br>
                                <span>Permit: {{ rateIncluded(rate.portPermit) }}</span>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="8"><div class="empty-rate-state">No logistics delivery rate cards are linked to this quotation.</div></td></tr>
                    </ng-template>
                </p-table>

            </div>
        }

        @if (activeTab() === 'job' && isPosmQuote(quote)) {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-briefcase text-primary"></i>
                    <span>POSM Job Details</span>
                </div>
                <div class="grid p-fluid" [formGroup]="posmForm">
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isInvalid(posmForm, 'projectName')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText id="projectName" formControlName="projectName" />
                                <label for="projectName">Job Name *</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (isInvalid(posmForm, 'projectName')) {
                            <small class="p-error block mt-1">Job name is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText id="eventLocation" formControlName="eventLocation" />
                                <label for="eventLocation">Job Location</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="brandId" formControlName="brandId" [options]="brandOptions()" optionLabel="name" optionValue="id" [filter]="true" [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="onBrandSelected($event.value)"></p-select>
                                <label for="brandId">Brand</label>
                            </p-iftaLabel>
                            <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" (click)="openBrandDialog()"></p-button>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-wrench"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="jobType" formControlName="jobType" [options]="posmJobTypeOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                <label for="jobType">Job Type</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-sort-amount-up"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="jobPriority" formControlName="jobPriority" [options]="priorityOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
                                <label for="jobPriority">Priority</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
                @if (quote.jobType === 'POSM_INSTALLATION') {
                    <div class="section-heading mt-4">
                        <i class="pi pi-wrench text-primary"></i>
                        <span>Installation Charges</span>
                    </div>
                    <p-table [value]="posmInstallationRows()" styleClass="p-datatable-sm p-datatable-gridlines logistics-rate-table" [tableStyle]="{ 'min-width': '60rem' }">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Description</th>
                                <th style="width: 10rem">Dimensions</th>
                                <th style="width: 8rem">Weight</th>
                                <th style="width: 7rem">Qty</th>
                                <th style="width: 10rem">Installation Cost</th>
                                <th>Remarks</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr>
                                <td><strong>{{ row.description || '-' }}</strong></td>
                                <td>{{ row.dimensions || '-' }}</td>
                                <td>{{ row.weight ? ((row.weight | number:'1.0-2') + ' kg') : '-' }}</td>
                                <td>{{ row.quantity || 1 }}</td>
                                <td><strong>{{ quote.currencyCode || 'SAR' }} {{ (row.installationCost || 0) | number:'1.2-2' }}</strong></td>
                                <td>{{ row.remarks || '-' }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="6"><div class="empty-rate-state">No POSM installation rows are linked to this quotation.</div></td></tr>
                        </ng-template>
                    </p-table>
                }
                <div class="flex justify-content-end mt-3">
                    <p-button label="Save Job Details" icon="pi pi-save" (onClick)="saveDetails()"></p-button>
                </div>
            </div>
        }

        @if (activeTab() === 'job' && isStorageQuote(quote)) {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-warehouse text-primary"></i>
                    <span>Warehouse Storage Charges</span>
                </div>
                <p-table [value]="storageRows()" styleClass="p-datatable-sm p-datatable-gridlines logistics-rate-table" [tableStyle]="{ 'min-width': '72rem' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th style="width: 11rem">Charge Type</th>
                            <th>Description</th>
                            <th style="width: 8rem">Metric</th>
                            <th style="width: 9rem">Rate</th>
                            <th style="width: 7rem">Qty</th>
                            <th style="width: 10rem">Amount</th>
                            <th>Remarks</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td><strong>{{ row.chargeType || '-' }}</strong></td>
                            <td>{{ row.description || '-' }}</td>
                            <td>{{ row.metric || '-' }}</td>
                            <td>{{ quote.currencyCode || 'SAR' }} {{ (row.rate || 0) | number:'1.2-2' }}</td>
                            <td>{{ row.quantity || 1 }}</td>
                            <td><strong>{{ quote.currencyCode || 'SAR' }} {{ (row.amount || ((row.rate || 0) * (row.quantity || 1))) | number:'1.2-2' }}</strong></td>
                            <td>{{ row.remarks || '-' }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="7"><div class="empty-rate-state">No warehouse storage charges are linked to this quotation.</div></td></tr>
                    </ng-template>
                </p-table>
            </div>
        }

        @if (activeTab() === 'terms') {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-file text-primary"></i>
                    <span>Terms And Conditions</span>
                </div>
                @if (quote.termsHtml) {
                    <div class="terms-content" [innerHTML]="quote.termsHtml"></div>
                } @else {
                    <div class="text-500">No terms snapshot has been saved for this quotation.</div>
                }
            </div>
        }

        @if (activeTab() === 'lines') {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-list text-primary"></i>
                    <span>Line Items</span>
                </div>
                <div class="line-action-bar">
                    <div class="line-total-cards">
                        <span>Sub Total <b>{{ linesTotals().subTotal | number:'1.2-2' }}</b></span>
                        <span>Discount <b>{{ linesTotals().discountAmount | number:'1.2-2' }}</b></span>
                        <span>VAT <b>{{ linesTotals().vatAmount | number:'1.2-2' }}</b></span>
                        <span class="grand-total">Total <b>{{ linesTotals().totalAmount | number:'1.2-2' }}</b></span>
                    </div>
                    <div class="flex gap-2">
                        <p-button label="Add Line" icon="pi pi-plus" severity="secondary" [outlined]="true" (onClick)="addLine()"></p-button>
                        <p-button label="Save Lines" icon="pi pi-save" (onClick)="saveLines()"></p-button>
                    </div>
                </div>
                <p-table [value]="lineControls" styleClass="p-datatable-sm p-datatable-gridlines" [tableStyle]="{ 'min-width': '72rem' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th style="width: 3rem">#</th>
                            <th>Product / Service</th>
                            <th style="width: 10rem">Category</th>
                            <th style="width: 7rem">UOM</th>
                            <th style="width: 8rem" class="text-right">Quantity</th>
                            <th style="width: 10rem" class="text-right">Price</th>
                            <th style="width: 9rem" class="text-right">Discount</th>
                            <th style="width: 8rem" class="text-right">Tax %</th>
                            <th style="width: 9rem" class="text-right">Total</th>
                            <th style="width: 4rem"></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line let-ri="rowIndex">
                        <tr [formGroup]="line">
                            <td>{{ ri + 1 }}</td>
                            <td [class.validation-field-invalid]="isLineInvalid(ri, 'description')">
                                <input pInputText formControlName="productName" class="w-full mb-2" placeholder="Product / service name">
                                <input pInputText formControlName="description" class="w-full" placeholder="Description">
                                @if (isLineInvalid(ri, 'description')) { <small class="p-error block mt-1">Description is required.</small> }
                            </td>
                            <td><p-select formControlName="itemCategory" [options]="categoryOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select></td>
                            <td><p-select formControlName="uom" [options]="uomOptions()" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select></td>
                            <td [class.validation-field-invalid]="isLineInvalid(ri, 'quantity')"><p-inputNumber formControlName="quantity" [min]="0" styleClass="w-full"></p-inputNumber></td>
                            <td [class.validation-field-invalid]="isLineInvalid(ri, 'unitPrice')"><p-inputNumber formControlName="unitPrice" [min]="0" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber></td>
                            <td><p-inputNumber formControlName="unitDiscount" [min]="0" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="2" styleClass="w-full"></p-inputNumber></td>
                            <td><p-inputNumber formControlName="taxRate" [min]="0" suffix="%" styleClass="w-full"></p-inputNumber></td>
                            <td class="text-right font-bold text-primary">{{ lineTotal(line.value) | currency:'SAR ' }}</td>
                            <td class="text-center"><button pButton icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" type="button" (click)="removeLine(ri)"></button></td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        }

        @if (activeTab() === 'documents') {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-paperclip text-primary"></i>
                    <span>Documents</span>
                </div>
                <div class="grid p-fluid mb-3" [formGroup]="attachmentForm">
                    <div class="field col-12 md:col-3" [class.validation-field-invalid]="isInvalid(attachmentForm, 'category')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-folder"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="category" formControlName="category" [options]="attachmentCategories" optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
                                <label for="category">Category</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (isInvalid(attachmentForm, 'category')) {
                            <small class="p-error block mt-1">Category is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-3" [class.validation-field-invalid]="isInvalid(attachmentForm, 'fileName')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText id="fileName" formControlName="fileName" />
                                <label for="fileName">Name</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (isInvalid(attachmentForm, 'fileName')) {
                            <small class="p-error block mt-1">Document name is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-4">
                        <label class="file-picker">
                            <input type="file" (change)="onAttachmentFileSelected($event)">
                            <span><i class="pi pi-upload"></i>{{ selectedAttachmentFile()?.name || 'Choose File' }}</span>
                        </label>
                    </div>
                    <div class="field col-12 md:col-2 flex align-items-end">
                        <p-button label="Add" icon="pi pi-plus" styleClass="w-full" [loading]="uploadingAttachment()" [disabled]="!selectedAttachmentFile()" (onClick)="saveAttachment()"></p-button>
                    </div>
                </div>
                <p-table [value]="attachments()" styleClass="p-datatable-sm p-datatable-gridlines">
                    <ng-template pTemplate="header"><tr><th>Category</th><th>Name</th><th>Version</th><th>Visibility</th><th></th></tr></ng-template>
                    <ng-template pTemplate="body" let-file>
                        <tr>
                            <td>{{ file.category }}</td>
                            <td><a [href]="file.fileUrl" target="_blank">{{ file.fileName }}</a></td>
                            <td>
                                @if (file.generated) {
                                    <p-tag [value]="'v' + (file.versionNumber || 1)" severity="info"></p-tag>
                                } @else {
                                    <span>-</span>
                                }
                            </td>
                            <td>{{ file.visibility || 'INTERNAL' }}</td>
                            <td class="text-right"><p-button icon="pi pi-trash" [text]="true" severity="danger" (onClick)="deleteAttachment(file)"></p-button></td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        }

        @if (activeTab() === 'communication') {
            <div class="grid">
                <div class="col-12 lg:col-7">
                    <div class="surface-card border-round-lg border-1 surface-border p-4 h-full">
                        <div class="section-heading">
                            <i class="pi pi-send text-primary"></i>
                            <span>External Review</span>
                        </div>
                        <div class="grid p-fluid" [formGroup]="submitForm">
                            <div class="field col-12 md:col-6" [class.validation-field-invalid]="isInvalid(submitForm, 'recipientCustomerId') || (!!submitForm.get('recipientCustomerId')?.value && !submitForm.get('recipientEmail')?.value)">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select inputId="recipientCustomerId"
                                                  formControlName="recipientCustomerId"
                                                  [options]="customerOptions()"
                                                  optionLabel="name"
                                                  optionValue="id"
                                                  [filter]="true"
                                                  [showClear]="true"
                                                  appendTo="body"
                                                  styleClass="w-full">
                                            <ng-template pTemplate="selectedItem" let-customer>
                                                {{ customer ? contactLabel(customer) : '' }}
                                            </ng-template>
                                            <ng-template pTemplate="item" let-customer>
                                                <div class="flex flex-column">
                                                    <span class="font-semibold">{{ customer.name }}</span>
                                                    <small class="text-500">{{ customer.emailAddress && customer.phoneNumber ? customer.emailAddress + ' | ' + customer.phoneNumber : (customer.emailAddress || customer.phoneNumber || 'No contact details') }}</small>
                                                </div>
                                            </ng-template>
                                        </p-select>
                                        <label for="recipientCustomerId">Recipient Contact</label>
                                    </p-iftaLabel>
                                    <p-button icon="pi pi-pencil"
                                              severity="secondary"
                                              [outlined]="true"
                                              styleClass="recipient-contact-button"
                                              [disabled]="!submitForm.get('recipientCustomerId')?.value"
                                              (onClick)="openEditRecipientDialog()"></p-button>
                                    <p-button icon="pi pi-plus" severity="secondary" [outlined]="true" styleClass="recipient-contact-button" (onClick)="openContactDialog()"></p-button>
                                </p-inputGroup>
                                @if (isInvalid(submitForm, 'recipientCustomerId')) {
                                    <small class="p-error block mt-1">Select a customer contact.</small>
                                }
                                @if (submitForm.get('recipientCustomerId')?.value && !submitForm.get('recipientEmail')?.value) {
                                    <small class="p-error block mt-1">Selected contact needs an email address.</small>
                                }
                                @if (submitForm.get('recipientEmail')?.value) {
                                    <small class="text-500 block mt-1">Email: {{ submitForm.get('recipientEmail')?.value }}</small>
                                }
                            </div>
                            <div class="field col-12 md:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-datepicker inputId="expiresAt" formControlName="expiresAt" styleClass="w-full"></p-datepicker>
                                        <label for="expiresAt">Token Expiry</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="submitComment" formControlName="comment" />
                                        <label for="submitComment">Comment</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="col-12">
                                <p-button label="Create Review Link" icon="pi pi-link" (onClick)="submitToClient()"></p-button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 lg:col-5">
                    <div class="surface-card border-round-lg border-1 surface-border p-4 h-full">
                        <div class="section-heading">
                            <i class="pi pi-envelope text-primary"></i>
                            <span>Email Status</span>
                        </div>
                        <div class="summary-grid mb-3">
                            <div><span>Status</span><p-tag [value]="quote.isEmailed ? 'Sent' : 'Draft'" [severity]="quote.isEmailed ? 'success' : 'secondary'"></p-tag></div>
                            <div><span>Last Sent</span><b>{{ quote.emailSentDate ? (quote.emailSentDate | date:'short') : '-' }}</b></div>
                            <div><span>Open Count</span><b>{{ quote.emailOpensCount || 0 }}</b></div>
                        </div>
                        <p-button label="Resend Quotation" icon="pi pi-send" severity="secondary" [outlined]="true" (onClick)="sendEmail()"></p-button>
                    </div>
                </div>
                <div class="col-12">
                    <div class="surface-card border-round-lg border-1 surface-border p-4">
                        <div class="section-heading">
                            <i class="pi pi-history text-primary"></i>
                            <span>Review Links</span>
                        </div>
                        <div class="flex flex-column gap-2">
                            @for (review of externalReviews(); track review.id) {
                                <div class="review-link-card">
                                    <div class="review-link-card__body">
                                        <div class="font-medium">{{ review.recipientEmail }}</div>
                                        <div class="text-sm text-500">{{ review.status }} {{ review.decision || '' }}</div>
                                    </div>
                                    @if (review.token) {
                                        <a class="review-link-card__url" [href]="quotationReviewUrl(review)" target="_blank" rel="noopener">
                                            {{ quotationReviewUrl(review) }}
                                        </a>
                                        <div class="review-link-card__actions">
                                            <p-button icon="pi pi-copy" label="Copy" severity="secondary" [outlined]="true" size="small" (onClick)="copyQuotationReviewLink(review)"></p-button>
                                            <p-button icon="pi pi-whatsapp" label="WhatsApp" severity="success" [outlined]="true" size="small" [disabled]="!canShareReviewOnWhatsapp(review)" (onClick)="shareQuotationReviewOnWhatsapp(review)"></p-button>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        }

        @if (activeTab() === 'remarks') {
            <div class="surface-card border-round-lg border-1 surface-border p-4">
                <div class="section-heading">
                    <i class="pi pi-comments text-primary"></i>
                    <span>Remarks</span>
                </div>
                <div class="grid p-fluid mb-3" [formGroup]="noteForm">
                    <div class="field col-12 md:col-7" [class.validation-field-invalid]="isInvalid(noteForm, 'noteText')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input pInputText id="noteText" formControlName="noteText" />
                                <label for="noteText">Note</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                        @if (isInvalid(noteForm, 'noteText')) {
                            <small class="p-error block mt-1">Note is required.</small>
                        }
                    </div>
                    <div class="field col-12 md:col-3">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-eye"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select inputId="visibility" formControlName="visibility" [options]="visibilityOptions" optionLabel="label" optionValue="value" styleClass="w-full"></p-select>
                                <label for="visibility">Visibility</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-2 flex align-items-end">
                        <p-button label="Add" icon="pi pi-plus" styleClass="w-full" (onClick)="saveReviewNote()"></p-button>
                    </div>
                </div>
                <div class="flex flex-column gap-2">
                    @for (note of reviewNotes(); track note.id) {
                        <div class="surface-50 border-round p-3">
                            <div class="flex justify-content-between text-sm text-500 mb-1">
                                <span>{{ note.stakeholderType || 'INTERNAL' }} / {{ note.visibility || 'INTERNAL' }}</span>
                                <span>{{ note.createdAt | date:'medium' }}</span>
                            </div>
                            <div>{{ note.noteText }}</div>
                        </div>
                    }
                </div>
            </div>
        }

        @if (activeTab() === 'tracking') {
            <div class="grid">
                <div class="col-12 lg:col-4">
                    <app-workflow-panel
                        resourceKey="CRM_QUOTATION"
                        [resourceId]="quote.id!"
                        [companyId]="quote.companyId"
                        [context]="{ totalAmount: quote.totalAmount, type: quote.type, jobType: quote.jobType || quote.type, customerId: quote.customerId, kamCustomerId: quote.kamCustomerId, approvalRequired: quote.approvalRequired, deviationStatus: quote.deviationStatus }">
                    </app-workflow-panel>
                </div>
                <div class="col-12 lg:col-8">
                    <div class="surface-card border-round-lg border-1 surface-border p-4 mb-3">
                        <div class="section-heading">
                            <i class="pi pi-list-check text-primary"></i>
                            <span>Requirements</span>
                        </div>
                        <div class="grid p-fluid mb-3" [formGroup]="requirementForm">
                            <div class="field col-12 md:col-6" [class.validation-field-invalid]="isInvalid(requirementForm, 'description')">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="requirementDescription" formControlName="description" />
                                        <label for="requirementDescription">Description</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                @if (isInvalid(requirementForm, 'description')) {
                                    <small class="p-error block mt-1">Requirement description is required.</small>
                                }
                            </div>
                            <div class="field col-12 md:col-2">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-share-alt"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="sourceType" formControlName="sourceType" />
                                        <label for="sourceType">Source</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-2">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-sort-amount-up"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="priority" formControlName="priority" />
                                        <label for="priority">Priority</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-2 flex align-items-end">
                                <p-button label="Add" icon="pi pi-plus" styleClass="w-full" (onClick)="saveRequirement()"></p-button>
                            </div>
                        </div>
                        <p-table [value]="requirements()" styleClass="p-datatable-sm p-datatable-gridlines">
                            <ng-template pTemplate="header"><tr><th>Description</th><th>Source</th><th>Priority</th><th>Status</th></tr></ng-template>
                            <ng-template pTemplate="body" let-item>
                                <tr><td>{{ item.description }}</td><td>{{ item.sourceType || '-' }} {{ item.sourceRecordId || '' }}</td><td>{{ item.priority || '-' }}</td><td>{{ item.status || '-' }}</td></tr>
                            </ng-template>
                        </p-table>
                    </div>

                    <div class="surface-card border-round-lg border-1 surface-border p-4">
                        <div class="section-heading">
                            <i class="pi pi-sitemap text-primary"></i>
                            <span>Internal Enrichment Tasks</span>
                        </div>
                        <div class="grid p-fluid mb-3" [formGroup]="taskForm">
                            <div class="field col-12 md:col-4" [class.validation-field-invalid]="isInvalid(taskForm, 'title')">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-check-square"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="taskTitle" formControlName="title" />
                                        <label for="taskTitle">Title</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                @if (isInvalid(taskForm, 'title')) {
                                    <small class="p-error block mt-1">Task title is required.</small>
                                }
                            </div>
                            <div class="field col-12 md:col-3">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="assignedTeam" formControlName="assignedTeam" />
                                        <label for="assignedTeam">Assigned Team</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-3">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-datepicker inputId="dueDate" formControlName="dueDate" styleClass="w-full"></p-datepicker>
                                        <label for="dueDate">Due Date</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-2 flex align-items-end">
                                <p-button label="Add" icon="pi pi-plus" styleClass="w-full" (onClick)="saveTask()"></p-button>
                            </div>
                            <div class="field col-12">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <input pInputText id="taskDescription" formControlName="description" />
                                        <label for="taskDescription">Description</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                        </div>
                        <p-table [value]="tasks()" styleClass="p-datatable-sm p-datatable-gridlines">
                            <ng-template pTemplate="header"><tr><th>Task</th><th>Team</th><th>Due</th><th>Status</th><th>Required</th></tr></ng-template>
                            <ng-template pTemplate="body" let-task>
                                <tr>
                                    <td><div class="font-medium">{{ task.title }}</div><div class="text-sm text-500">{{ task.description }}</div></td>
                                    <td>{{ task.assignedTeam || '-' }}</td>
                                    <td>{{ task.dueDate | date:'mediumDate' }}</td>
                                    <td><p-select [ngModel]="task.status" [options]="taskStatuses" optionLabel="label" optionValue="value" styleClass="w-full" (onChange)="updateTaskStatus(task, $event.value)"></p-select></td>
                                    <td>{{ task.requiredForSubmission === false ? 'No' : 'Yes' }}</td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                </div>
            </div>
        }
        </div>
    </app-page-data-panel>
</div>

<p-dialog header="Create C&F Execution" [modal]="true" [visible]="cfHandoffVisible()" (visibleChange)="cfHandoffVisible.set($event)" [style]="{ width: '58rem' }" [breakpoints]="{ '768px': '94vw' }">
    <form [formGroup]="cfHandoffForm" class="form-stack">
        <div class="grid p-fluid">
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-file-plus"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="createSalesOrder" formControlName="createSalesOrder" [options]="yesNoOptions" optionLabel="label" optionValue="value" (onChange)="loadCfExecutionPreview()" styleClass="w-full"></p-select>
                        <label for="createSalesOrder">Create Sales Order</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-credit-card"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="cfPaymentTermId" formControlName="paymentTermId" [options]="paymentTerms()" optionLabel="name" optionValue="paymentTermID" [showClear]="true" (onChange)="loadCfExecutionPreview()" styleClass="w-full"></p-select>
                        <label for="cfPaymentTermId">Payment Term</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="createClearance" formControlName="createClearance" [options]="yesNoOptions" optionLabel="label" optionValue="value" (onChange)="loadCfExecutionPreview()" styleClass="w-full"></p-select>
                        <label for="createClearance">Create Clearance</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="createContainerCase" formControlName="createContainerCase" [options]="yesNoOptions" optionLabel="label" optionValue="value" (onChange)="loadCfExecutionPreview()" styleClass="w-full"></p-select>
                        <label for="createContainerCase">Create Container Case</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="applyTemplate" formControlName="applyTemplate" [options]="yesNoOptions" optionLabel="label" optionValue="value" (onChange)="loadCfExecutionPreview()" styleClass="w-full"></p-select>
                        <label for="applyTemplate">Apply Milestones</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-paperclip"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select inputId="applyDocumentRequirements" formControlName="applyDocumentRequirements" [options]="yesNoOptions" optionLabel="label" optionValue="value" (onChange)="loadCfExecutionPreview()" styleClass="w-full"></p-select>
                        <label for="applyDocumentRequirements">Apply Documents</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="cfOverrideReason" pInputText formControlName="overrideReason" (blur)="loadCfExecutionPreview()">
                        <label for="cfOverrideReason">Override Reason</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
        </div>
    </form>

    @if (cfHandoffLoading()) {
        <p-progressBar mode="indeterminate"></p-progressBar>
    }

    @if (cfHandoffPreview(); as preview) {
        <div class="job-summary-grid mt-3">
            <div><span>Project</span><b>{{ preview.projectExists ? 'Reuse existing' : 'Create new' }}</b></div>
            <div><span>Project Type</span><b>{{ preview.projectType || '-' }}</b></div>
            <div><span>Template</span><b>{{ preview.templateCode || '-' }}</b></div>
            <div><span>C&F Profile</span><b>{{ preview.profileExists ? 'Reuse/update' : 'Create' }}</b></div>
            <div><span>Sales Order</span><b>{{ preview.salesOrderExists ? 'Reuse existing' : preview.salesOrderPlanned ? 'Create' : 'Skip' }}</b></div>
            <div><span>Clearance</span><b>{{ preview.clearanceExists ? 'Reuse existing' : preview.clearancePlanned ? 'Create' : 'Skip' }}</b></div>
            <div><span>Container Case</span><b>{{ preview.containerCaseExists ? 'Reuse existing' : preview.containerCasePlanned ? 'Create' : 'Skip' }}</b></div>
            <div><span>Status</span><b>{{ preview.handoffAllowed ? 'Ready' : 'Blocked' }}</b></div>
        </div>
        @if (preview.warnings?.length) {
            <p-table [value]="preview.warnings" styleClass="p-datatable-sm p-datatable-gridlines mt-3">
                <ng-template pTemplate="header"><tr><th>Severity</th><th>Code</th><th>Message</th></tr></ng-template>
                <ng-template pTemplate="body" let-warning>
                    <tr>
                        <td><p-tag [value]="warning.severity" [severity]="warning.severity === 'ERROR' ? 'danger' : warning.severity === 'WARN' ? 'warn' : 'info'"></p-tag></td>
                        <td>{{ warning.code }}</td>
                        <td>{{ warning.message }}</td>
                    </tr>
                </ng-template>
            </p-table>
        }
    }

    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="cfHandoffVisible.set(false)"></p-button>
        <p-button label="Refresh Preview" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="cfHandoffLoading()" (click)="loadCfExecutionPreview()"></p-button>
        <p-button label="Create Execution" icon="pi pi-check" [disabled]="cfHandoffPreview()?.handoffAllowed === false || cfHandoffExecuting()" [loading]="cfHandoffExecuting()" (click)="createCfExecutionFromQuotation()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="Reject Quotation" [(visible)]="showRejectDialog" [modal]="true" [style]="{ width: '450px' }">
    <div class="flex flex-column gap-2 mt-2">
        <label for="rejectReason" class="font-bold text-red-600">Rejection Reason *</label>
        <textarea id="rejectReason" pTextarea [(ngModel)]="rejectReason" rows="5" placeholder="Specify why the quotation was rejected..."></textarea>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="showRejectDialog.set(false)"></p-button>
        <p-button label="Confirm Reject" icon="pi pi-check" severity="danger" (onClick)="rejectQuotation()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="New Brand" [modal]="true" [visible]="brandDialogVisible()" (visibleChange)="brandDialogVisible.set($event)" [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '94vw' }">
    <form [formGroup]="brandForm" class="form-stack">
        <div class="grid p-fluid">
            <div class="field col-12" [class.validation-field-invalid]="isBrandInvalid('name')">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="newBrandName" pInputText formControlName="name">
                        <label for="newBrandName">Brand Name *</label>
                    </p-iftaLabel>
                </p-inputGroup>
                @if (isBrandInvalid('name')) {
                    <small class="p-error block mt-1">Brand name is required.</small>
                }
            </div>
            <div class="field col-12">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="newBrandCode" pInputText formControlName="code">
                        <label for="newBrandCode">Brand Code</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
        </div>
    </form>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="closeBrandDialog()"></p-button>
        <p-button label="Create Brand" icon="pi pi-check" [loading]="savingBrand()" [disabled]="savingBrand()" (click)="createBrand()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog [header]="editingContact() ? 'Edit Quotation Contact' : 'New Quotation Contact'" [modal]="true" [visible]="contactDialogVisible()" (visibleChange)="contactDialogVisible.set($event)" [style]="{ width: '34rem' }" [breakpoints]="{ '640px': '94vw' }">
    <form [formGroup]="contactForm" class="form-stack">
        <div class="grid p-fluid">
            <div class="field col-12" [class.validation-field-invalid]="isContactInvalid('name')">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="newContactName" pInputText formControlName="name">
                        <label for="newContactName">Contact Name *</label>
                    </p-iftaLabel>
                </p-inputGroup>
                @if (isContactInvalid('name')) {
                    <small class="p-error block mt-1">Contact name is required.</small>
                }
            </div>
            <div class="field col-12">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="newContactEmail" pInputText formControlName="emailAddress">
                        <label for="newContactEmail">Email</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
            <div class="field col-12">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="newContactPhone" pInputText formControlName="phoneNumber">
                        <label for="newContactPhone">Phone</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
        </div>
    </form>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="closeContactDialog()"></p-button>
        <p-button [label]="editingContact() ? 'Save Contact' : 'Create Contact'" icon="pi pi-check" [loading]="savingContact()" [disabled]="savingContact()" (click)="createContact()"></p-button>
    </ng-template>
</p-dialog>
</app-page-shell>
}
